import { api } from "@/lib/api";
import { Transaction, ApiResponse, ApiResponseWithItems } from "@/lib/types";
import { formatDateToISOString } from "@/lib/utils";
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";

export const useGetTransactions = ({
  vesselId,
  options,
}: {
  vesselId: string | undefined;
  options?: Omit<
    UseQueryOptions<ApiResponse<Transaction[]>>,
    "queryKey" | "queryFn"
  >;
}) => {
  return useQuery<ApiResponse<Transaction[]>>({
    queryKey: ["transactions"],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Transaction[]>>(
        `/transactions?vesselId=${vesselId}`
      );
      return response.data;
    },
    ...options,
  });
};

export const useGetTransactionsByDateRange = ({
  fromDate,
  toDate,
  vesselId,
  options,
}: {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  vesselId?: string;
  options?: Omit<
    UseQueryOptions<ApiResponse<Transaction[]>>,
    "queryKey" | "queryFn"
  >;
}) => {
  return useQuery<ApiResponse<Transaction[]>>({
    queryKey: ["transactions-by-date-range", fromDate, toDate, vesselId],
    queryFn: async () => {
      const fromDateString = fromDate
        ? formatDateToISOString(startOfDay(fromDate))
        : "";
      const toDateString = toDate
        ? formatDateToISOString(endOfDay(toDate))
        : "";
      const response = await api.get<ApiResponse<Transaction[]>>(
        `/transactions/by-date-range?startDate=${fromDateString}&endDate=${toDateString}&${
          vesselId ? `vesselId=${vesselId}` : ""
        }`
      );
      return response.data;
    },
    ...options,
  });
};

/**
 * Fetch a transaction by id.
 *
 * BE policy: when a CHILD transaction id is requested, the API returns the
 * PARENT entity instead (child transactions like bank commissions must never
 * be loaded or edited independently of their parent). Callers receive
 * `canonicalId` / `isCanonical` so they can `navigate(..., { replace: true })`
 * to the parent's URL before binding the detail view to the payload.
 */
export const useGetTransactionById = (
  id: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<Transaction>>,
    "queryKey" | "queryFn"
  >
) => {
  const queryClient = useQueryClient();
  const query = useQuery<ApiResponse<Transaction>>({
    queryKey: ["transaction", id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Transaction>>(
        `/transactions/${id}`
      );
      const value = response.data?.value;
      // If BE redirected us to a parent, seed the parent's cache key so the
      // post-replace render is instant.
      if (value && value.id && value.id !== id) {
        queryClient.setQueryData(["transaction", value.id], response.data);
      }
      return response.data;
    },
    ...options,
  });
  const canonicalId = query.data?.value?.id;
  const isCanonical = !!canonicalId && canonicalId === id;
  return { ...query, canonicalId, isCanonical };
};

export const useGetTransactionsBySearch = ({
  searchParams,
  options,
}: {
  searchParams: string;
  options?: Omit<
    UseQueryOptions<ApiResponseWithItems<Transaction>>,
    "queryKey" | "queryFn"
  >;
}) => {
  return useQuery<ApiResponseWithItems<Transaction>>({
    queryKey: ["transactions-by-search", searchParams],
    queryFn: async () => {
      const response = await api.get<ApiResponseWithItems<Transaction>>(
        `/transactions/search?${searchParams}`
      );
      return response.data;
    },
    ...options,
  });
};

export const useGetTransactionAttachmentURLById = (
  id: string,
  options?: Omit<UseQueryOptions<ApiResponse<string>>, "queryKey" | "queryFn">
) => {
  return useQuery<ApiResponse<string>>({
    queryKey: ["transaction-attachment-url-by-id", id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<string>>(
        `/transactions/attachments/${id}`
      );
      return response.data;
    },
    ...options,
  });
};
